// components/ui/Tabs.tsx
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

// --- TYPES ---
interface TabsContextType {
  activeTab: string | null;
  setActiveTab: (tab: string | null) => void;
}

interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
  defaultTab?: string;
  children: ReactNode;
}

interface TabProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
  activeClassNames?: string;
  children: ReactNode;
}

interface TabContentProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  children: ReactNode;
}

// --- CONTEXT ---
const TabsContext = createContext<TabsContextType | undefined>(undefined);

const useTabs = (): TabsContextType => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('useTabs must be used within a <Tabs> component');
  }
  return context;
};

// --- TABS ROOT ---
export const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
  ({ defaultTab, children, className, ...props }, ref) => {
    const [activeTab, setActiveTab] = useState<string | null>(defaultTab ?? null);

    useEffect(() => {
      setActiveTab(defaultTab ?? null);
    }, [defaultTab]);

    return (
      <TabsContext.Provider value={{ activeTab, setActiveTab }}>
        <div ref={ref} className={className} {...props}>
          {children}
        </div>
      </TabsContext.Provider>
    );
  }
);
Tabs.displayName = 'Tabs';

// --- TABS LIST ---
export const TabsList = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} {...props} className={`flex border-b border-gray-200 mb-3 ${className}`}>
      {children}
    </div>
  )
);
TabsList.displayName = 'TabsList';

// --- TAB BUTTON ---
export const Tab = React.forwardRef<HTMLButtonElement, TabProps>(
  ({ value, children, className, activeClassNames, ...props }, ref) => {
    const { activeTab, setActiveTab } = useTabs();
    const isActive = activeTab === value;

    return (
      <button
        ref={ref}
        {...props}
        onClick={() => setActiveTab(value)}
        className={`px-4 py-2 text-primary text-sm font-semibold ${
          isActive ? activeClassNames : ''
        } ${className} `}
      >
        {children}
      </button>
    );
  }
);
Tab.displayName = 'Tab';

// --- TAB CONTENT ---
export const TabContent = React.forwardRef<HTMLDivElement, TabContentProps>(
  ({ value, children, className, ...props }, ref) => {
    const { activeTab } = useTabs();

    if (activeTab !== value) return null;

    return (
      <div ref={ref} className={`'py-3 ${className}`} {...props}>
        {children}
      </div>
    );
  }
);
TabContent.displayName = 'TabContent';
